import React, { memo, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { addPageSize } from "core/utils/constants";
import httpclient from "libs/Axios";
import { getCurrentDate } from "core/utils/constants/DateTime";
import { useAuth } from "core/context/AuthContext/AuthContextProvider";
import { formatAmount } from "core/utils/constants";
import { SetupLoader } from "core/components/SetupComponents/SetupLoader";
import GoogleTranslate from "core/components/GoogleTranslate";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

interface FeeSchedule {
  ScheduleName: string;
  DueDate: string;
  CategoryList: Array<{
    CanAddInTotal: boolean;
    CategoryName: string;
    TotalFee: number;
    PaidFee: number;
    Concession: number;
    Due: number;
  }>;
}

interface FeeData {
  FullName: string;
  Class: string;
  RollNo: string;
  AdmissionNo: string;
  Fee: {
    total: number;
    paid: number;
    concession: number;
    due: number;
    Schedules: FeeSchedule[];
    Arrears: any;
  };
  FeePaymentDuration: any;
}

const FeeDetailsView = memo(({feeData}: {feeData?: any}) => {
  const authData = useAuth();
  const params = useParams();
  const [data, setData] = useState<FeeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoader, setPDFLoader] = useState(false);
  const [pdfOrientation, setPdfOrientation] = useState<'p' | 'l'>('p'); // Default to Portrait

  useEffect(() => {
    const fetchFeeDetails = async () => {
      try {
        setLoading(true);
        const response = await httpclient().get(
          addPageSize(
            `/data/feeStudentCategory/view?student_id=${params?.studentId}&academic_year_id=${+authData?.CURRENT_ACADEMIC_YEAR_ID}&today_date=${getCurrentDate(
              new Date(),
              true
            )}&student_class_id=${params?.studentClassId}`
          )
        );
        setData(response?.data?.data);
      } catch (err) {
        setError("Failed to fetch fee details");
        console.error("Error fetching fee details:", err);
      } finally {
        setLoading(false);
      }
    };

    if (params?.studentId && params?.studentClassId && authData?.BRANCH_ID) {
      fetchFeeDetails();
    } else {
       if(!!feeData) {
        setData(feeData);
        setLoading(() => false);
      }
    }
  }, [params?.studentId, params?.studentClassId, authData?.BRANCH_ID, authData?.CURRENT_ACADEMIC_YEAR_ID, !!feeData]);

  const getScheduleTotal = (categoryList: any[]) => {
    return {
      totalFee: categoryList.filter((cat) => cat.CanAddInTotal).reduce((sum, cat) => sum + cat.TotalFee, 0),
      totalPaid: categoryList.filter((cat) => cat.CanAddInTotal).reduce((sum, cat) => sum + cat.PaidFee, 0),
      totalConcession: categoryList.filter((cat) => cat.CanAddInTotal).reduce((sum, cat) => sum + cat.Concession, 0),
      totalDue: categoryList.filter((cat) => cat.CanAddInTotal).reduce((sum, cat) => sum + cat.Due, 0),
    };
  };

  const handleExportPDF = async () => {
    try {
      setPDFLoader(true);

      // Get the main container by id
      const mainContent = document.getElementById('yourExportToPDFFileData');

      if (!mainContent) {
        console.error('Export content not found');
        return;
      }

      // Hide the Google Translate element and export button during PDF generation
      const googleTranslateWrapper = mainContent.previousElementSibling as HTMLElement;
      const exportButton = document.getElementById('exportPdfButton');

      const originalTranslateDisplay = googleTranslateWrapper?.style.display;
      const originalButtonDisplay = exportButton?.style.display;

      if (googleTranslateWrapper) {
        googleTranslateWrapper.style.display = 'none';
      }
      if (exportButton) {
        exportButton.style.display = 'none';
      }

      // Render at a fixed A4 width so the PDF does not depend on the user's
      // screen or modal width. This also ensures horizontally overflowing
      // tables are included on smaller machines.
      const exportWidth = pdfOrientation === 'p' ? 794 : 1123;
      const canvas = await html2canvas(mainContent as HTMLElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: exportWidth,
        windowWidth: exportWidth,
        scrollX: 0,
        scrollY: 0,
        onclone: clonedDocument => {
          const clonedContent = clonedDocument.getElementById('yourExportToPDFFileData');
          if (clonedContent) {
            clonedContent.style.width = `${exportWidth}px`;
            clonedContent.style.maxWidth = 'none';
            clonedContent.style.margin = '0';
            clonedContent.style.overflow = 'visible';
          }
        },
      });

      // Restore hidden elements
      if (googleTranslateWrapper && originalTranslateDisplay !== undefined) {
        googleTranslateWrapper.style.display = originalTranslateDisplay;
      }
      if (exportButton && originalButtonDisplay !== undefined) {
        exportButton.style.display = originalButtonDisplay;
      }

      // Use a real A4 page and scale the complete capture within its margins.
      const pdf = new jsPDF({ orientation: pdfOrientation, unit: 'mm', format: 'a4' });
      const margin = 8;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - (margin * 2);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Convert canvas to image
      const imgData = canvas.toDataURL('image/png');

      // Add further A4 pages when the report is taller than one page.
      const printableHeight = pageHeight - (margin * 2);
      let remainingHeight = imgHeight;
      let imageY = margin;

      pdf.addImage(imgData, 'PNG', margin, imageY, imgWidth, imgHeight);
      remainingHeight -= printableHeight;

      while (remainingHeight > 0) {
        pdf.addPage();
        imageY -= printableHeight;
        pdf.addImage(imgData, 'PNG', margin, imageY, imgWidth, imgHeight);
        remainingHeight -= printableHeight;
      }

      // Generate filename with student name and date
      const studentName = data?.FullName?.replace(/\s+/g, '_') || 'Student';
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `Fee_Details_${studentName}_${currentDate}.pdf`;

      // Save PDF
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      // You can add a toast notification here to inform the user
    } finally {
      setPDFLoader(false);
    }
  };

  if (loading) return <div className="w-full h-[40vh] flex justify-center items-center"><SetupLoader /></div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;
  if (!data) return <div className="p-4">No data available</div>;

  return (
    <>
      <div className="flex justify-between items-center max-w-5xl mx-auto">
        <div className="w-[20%]">
          <GoogleTranslate />
        </div>
        {/* Export PDF Button with Orientation Toggle */}
        <div id="exportPdfButton" className="flex items-center gap-4 my-4">
          {/* Orientation Toggle */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-2">
            <span className="text-sm font-medium text-gray-700">Orientation:</span>
            <button
              onClick={() => setPdfOrientation('p')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                pdfOrientation === 'p'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-200'
              }`}
            >
              Portrait
            </button>
            <button
              onClick={() => setPdfOrientation('l')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                pdfOrientation === 'l'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-200'
              }`}
            >
              Landscape
            </button>
          </div>

          {/* Export Button */}
          <button
            onClick={handleExportPDF}
            disabled={pdfLoader}
            className="px-6 py-2 bg-blue-600 text-[white] rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pdfLoader ? "Loading ..." : <><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
              Export as PDF</>}
          </button>
        </div>
      </div>


      <div id="yourExportToPDFFileData" className="p-6 max-w-5xl mx-auto mt-4 rounded-lg bg-white print:m-0 print:mx-auto print:p-5">
        {/* Student Details Section */}
        <div className="w-full bg-gray-200 rounded-lg p-6 flex justify-between items-center gap-4 print:break-inside-avoid">
          <div className="flex flex-col">
            <span className="text-base text-gray-500">Student Name</span>
            <span className="font-medium text-lg">{data.FullName}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-base text-gray-500">Roll Number</span>
            <span className="font-medium text-lg">{data.RollNo}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-base text-gray-500">Adm No</span>
            <span className="font-medium text-lg">{data.AdmissionNo}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-base text-gray-500">Class</span>
            <span className="font-medium text-lg">{data.Class}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-base text-gray-500">Duration Name</span>
            <span className="font-medium text-lg">{data.FeePaymentDuration?.DURATION_NAME}</span>
          </div>
        </div>

        {/* Arrears Section */}
        {data?.Fee?.Arrears?.length > 0 && (
          <>
            <h3 className="text-center text-2xl font-bold my-4">Arrears</h3>
            <div className="border border-gray-400">
              <table className="w-full border-collapse bg-white print:w-full print:mx-auto">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border border-gray-400 p-4 text-left">Type of Fee</th>
                    <th className="border border-gray-400 p-4 text-right">Fees Amount</th>
                    <th className="border border-gray-400 p-4 text-right">Paid Fees</th>
                    <th className="border border-gray-400 p-4 text-right">Concession</th>
                    <th className="border border-gray-400 p-4 text-right">Due Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.Fee?.Arrears?.map((item, index) => (
                    <tr key={index}>
                      <td className="border border-gray-400 p-4 text-left">{item?.NAME}</td>
                      <td className="border border-gray-400 p-4 text-right">{formatAmount(authData, item?.AMOUNT)}</td>
                      <td className="border border-gray-400 p-4 text-right">{formatAmount(authData, item?.PAYMENT)}</td>
                      <td className="border border-gray-400 p-4 text-right">{formatAmount(authData, item?.CONCESSION)}</td>
                      <td className="border border-gray-400 p-4 text-right">{formatAmount(authData, item?.DUE)}</td>
                    </tr>
                  ))}
                  {/* Total Row */}
                  <tr className="bg-gray-200">
                    <td className="p-4 font-medium">Total</td>
                    <td className="p-4 text-right font-medium">
                      {formatAmount(authData, data?.Fee?.Arrears?.reduce((sum, item) => sum + item?.AMOUNT, 0))}
                    </td>
                    <td className="p-4 text-right font-medium">
                      {formatAmount(authData, data?.Fee?.Arrears?.reduce((sum, item) => sum + item?.PAYMENT, 0))}
                    </td>
                    <td className="p-4 text-right font-medium">
                      {formatAmount(authData, data?.Fee?.Arrears?.reduce((sum, item) => sum + item?.CONCESSION, 0))}
                    </td>
                    <td className="p-4 text-right font-medium">
                      {formatAmount(authData, data?.Fee?.Arrears?.reduce((sum, item) => sum + item?.DUE, 0))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Regular Section */}
        <h3 className="text-center text-2xl font-bold my-4">Regular</h3>
        <div className="border border-gray-400">
          <table className="w-full border-collapse bg-white print:w-full print:mx-auto">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-gray-400 p-4 text-left">Schedule & Due Date</th>
                <th className="border border-gray-400 p-4 text-left">Type of Fee</th>
                <th className="border border-gray-400 p-4 text-right">Fees Amount</th>
                <th className="border border-gray-400 p-4 text-right">Paid Fees</th>
                <th className="border border-gray-400 p-4 text-right">Concession</th>
                <th className="border border-gray-400 p-4 text-right">Due Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.Fee.Schedules.map((schedule, index) => {
                const scheduleTotal = getScheduleTotal(schedule.CategoryList);
                return (
                  <React.Fragment key={index}>
                    {schedule.CategoryList.map((category, i) => (
                      <tr key={i}>
                        {i === 0 && (
                          <td className="border border-gray-400 p-4 text-left font-medium" rowSpan={schedule.CategoryList.length}>
                            {schedule.ScheduleName}
                            <br />
                            <span className="text-gray-600">{schedule.DueDate}</span>
                          </td>
                        )}
                        <td className="border border-gray-400 p-4 text-left">{category?.CanAddInTotal ? category?.CategoryName : `${category?.CategoryName}*`}</td>
                        <td className="border border-gray-400 p-4 text-right">
                          {formatAmount(authData, category.TotalFee)}
                        </td>
                        <td className="border border-gray-400 p-4 text-right">
                          {formatAmount(authData, category.PaidFee)}
                        </td>
                        <td className="border border-gray-400 p-4 text-right">
                          {formatAmount(authData, category.Concession)}
                        </td>
                        <td className="border border-gray-400 p-4 text-right">
                          {formatAmount(authData, category.Due)}
                        </td>
                      </tr>
                    ))}
                    {/* Total Row */}
                    <tr className="bg-gray-200">
                      <td className="p-4 font-medium" colSpan={2}>
                        Total
                      </td>
                      <td className="p-4 text-right font-medium">{formatAmount(authData, scheduleTotal.totalFee)}</td>
                      <td className="p-4 text-right font-medium">{formatAmount(authData, scheduleTotal.totalPaid)}</td>
                      <td className="p-4 text-right font-medium">{formatAmount(authData, scheduleTotal.totalConcession)}</td>
                      <td className="p-4 text-right font-medium">{formatAmount(authData, scheduleTotal.totalDue)}</td>
                    </tr>
                  </React.Fragment>
                );
              })}
              <tr className="bg-gray-200">
                <td className="p-4 font-medium" colSpan={2}>GrandTotal</td>
                <td className="p-4 text-right font-medium">{formatAmount(authData, data.Fee.total)}</td>
                <td className="p-4 text-right font-medium">{formatAmount(authData, data.Fee.paid)}</td>
                <td className="p-4 text-right font-medium">{formatAmount(authData, data.Fee.concession)}</td>
                <td className="p-4 text-right font-medium">{formatAmount(authData, data.Fee.due)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-md my-2">Note: * This category is not included in the total.</p>
      </div>
    </>
  );
});

export default FeeDetailsView;
